import React from 'react';
import { Text, Button } from '@chaser/components';
import BottomNotification from '../index';
import ConnectService from '../../../services/connect-service';
import { IntegrationDisplayNames, Integrations } from '../../../util/constants';
import usePush from '../../../hooks/usePush';
import { useHistory } from 'react-router';
import AccountsIQModal from '../../../app/connect/components/AccountsIQModal';
import useModal from '../../../hooks/useModal';
import useOrganisation from '../../../hooks/useOrganisation';
import { ConnectToXeroButton } from '../../XeroButton';
import OdooModal from '../../../app/connect/components/OdooModal';
import StripeModal from '../../../app/connect/components/StripeModal';
import SageIntacctModal from '../../../app/connect/components/SageIntacctModal';
import useShortCode from '@/hooks/useShortCode';

function AccountsIQReconnect({ accountingSoftware }) {
  const showModal = useModal();
  const { OrganisationID, accessToken } = useOrganisation();

  return (
    <Button
      onClick={() =>
        showModal(<AccountsIQModal companyId={OrganisationID} region={accessToken?.region} />)
      }
    >
      Reconnect to {IntegrationDisplayNames[accountingSoftware]}
    </Button>
  );
}

function OdooReconnect() {
  const showModal = useModal();
  const { ShortCode, accessToken } = useOrganisation();

  return (
    <Button
      onClick={() => showModal(<OdooModal ShortCode={ShortCode} odooConfiguration={accessToken} />)}
    >
      Reconnect to {IntegrationDisplayNames[Integrations.ODOO]}
    </Button>
  );
}

function StripeReconnect() {
  const showModal = useModal();

  return (
    <Button onClick={() => showModal(<StripeModal />)}>
      Reconnect to {IntegrationDisplayNames[Integrations.STRIPE]}
    </Button>
  );
}

function SageIntacctReconnect() {
  const showModal = useModal();
  const { accessToken } = useOrganisation();
  const { companyId, userId } = accessToken || {};
  return (
    <Button onClick={() => showModal(<SageIntacctModal reconnect companyId={companyId} userId={userId}/>)}>
      Reconnect to {IntegrationDisplayNames[Integrations.SAGEINTACCT]}
    </Button>
  );
}

function ConnectionButton({ accountingSoftware }) {
  const history = useHistory();
  const shortCode = useShortCode();

  const { execute } = usePush(ConnectService.connect, {
    errorToast: 'Failed to connect to accounting software',
  });

  async function connectToAccountingSoftware() {
    const [err, res] = await execute(accountingSoftware, shortCode);
    if (err) return;
    window.location = res;
  }

  switch (accountingSoftware) {
    case Integrations.ACCOUNTSIQ:
      return <AccountsIQReconnect accountingSoftware={accountingSoftware} />;
    case Integrations.ODOO:
      return <OdooReconnect />;
    case Integrations.STRIPE:
      return <StripeReconnect />;
    case Integrations.SAGEINTACCT:
      return <SageIntacctReconnect />;
    case Integrations.OPENAPI:
    case Integrations.ZAPIER:
    case Integrations.SAGE50:
    case Integrations.QUICKBOOKS:
      return (
        <Button
          onClick={() => history.push('/settings/integrations')}
          data-testid="confirmBtn"
          data-trackingid="confirmBtn"
        >
          Go to Integrations
        </Button>
      );
    case Integrations.XERO:
      return (
        <ConnectToXeroButton
          onClick={connectToAccountingSoftware}
          data-testid="welcome-connect-button"
          data-trackingid="welcome-connect-button"
        />
      );
    default:
      return (
        <Button onClick={connectToAccountingSoftware}>
          Reconnect to {IntegrationDisplayNames[accountingSoftware] || accountingSoftware}
        </Button>
      );
  }
}

function ConnectionText({ accountingSoftware }) {
  switch (accountingSoftware) {
    case Integrations.OPENAPI:
    case Integrations.ZAPIER:
      return (
        <Text color="White">
          You are not connected. Get your API keys and use them to connect to Chaser.
        </Text>
      );
    case Integrations.SAGE50:
      return (
        <Text color="White">
          Download Chaser Connector, get your API keys and use them to connect to Chaser.
        </Text>
      );
    default:
      return <Text color="White">You are not connected to an accounting software</Text>;
  }
}

const ConnectToAccountingNotification = ({ accountingSoftware, sidebarCollapsed, hasSidebar }) => {
  return (
    <BottomNotification
      sidebarCollapsed={sidebarCollapsed}
      hasSidebar={hasSidebar}
      left={<ConnectionText accountingSoftware={accountingSoftware} />}
      right={<ConnectionButton accountingSoftware={accountingSoftware} />}
    />
  );
};

export default ConnectToAccountingNotification;
